import { Toggle as TogglePrimitive } from "@base-ui/react/toggle";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "../../utils/cn";

import { textVariants } from "./text";

// Size → Text variant — same pattern as Button. Typography is owned by
// the Text variant per size; the cva embeds the variant's class chain.
const SIZE_TO_TEXT_VARIANT = {
    default: "body",
    sm: "small",
    lg: "body"
} as const;

const toggleVariants = cva(
    cn(
        "group/toggle inline-flex items-center justify-center gap-1 rounded-none",
        "whitespace-nowrap text-foreground-base",
        "transition-all outline-none",
        "hover:bg-surface-sunken hover:text-foreground-base",
        // Base UI surfaces the disabled state via both the HTML `disabled`
        // attribute AND `data-disabled`. Cover both so the hover/opacity
        // overrides land regardless of which Tailwind matches first.
        // `pointer-events-none` is the load-bearing piece that prevents
        // `:hover` from triggering.
        "disabled:pointer-events-none disabled:opacity-50",
        "data-disabled:pointer-events-none data-disabled:opacity-50",
        "aria-invalid:border-error-base aria-invalid:ring-status-error-200",
        "aria-pressed:bg-surface-sunken data-[state=on]:bg-surface-sunken",
        "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
    ),
    {
        variants: {
            variant: {
                default: "bg-transparent",
                outline:
                    "border border-line-base bg-transparent hover:bg-surface-sunken"
            },
            size: {
                default: cn(
                    textVariants({ variant: SIZE_TO_TEXT_VARIANT.default }),
                    "h-8 min-w-8 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2"
                ),
                sm: cn(
                    textVariants({ variant: SIZE_TO_TEXT_VARIANT.sm }),
                    "h-7 min-w-7 px-2.5 has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3.5"
                ),
                lg: cn(
                    textVariants({ variant: SIZE_TO_TEXT_VARIANT.lg }),
                    "h-9 min-w-9 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2"
                )
            }
        },
        defaultVariants: {
            variant: "default",
            size: "default"
        }
    }
);

function Toggle({
    className,
    variant = "default",
    size = "default",
    ...props
}: TogglePrimitive.Props &
    VariantProps<typeof toggleVariants>): React.ReactElement {
    return (
        <TogglePrimitive
            data-slot="toggle"
            className={cn(toggleVariants({ variant, size, className }))}
            {...props}
        />
    );
}

export { Toggle, toggleVariants };
